import React from 'react';
import {
  Box, Button, ButtonText, Icon, Spinner, Text, VStack,
} from '@gluestack-ui/themed';
import { AlertTriangle } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import { LobbyOrientationPlayer } from './LobbyOrientationPlayer';
import { OrientationRejectOverlay } from './OrientationRejectOverlay';
import type { LobbyStepProps } from './types';

/**
 * Lobby step — skippable orientation film before the waiting hold (A1).
 * No checklist, tips, clock, or mute on this step.
 */
export function LobbyOrientationStep({
  runState,
  orientationVideoUrl,
  loungeOrientationResolved = false,
  loungeFetchFailed = false,
  onOrientationComplete,
  isCompletingOrientation = false,
  orientationStampRetrying = false,
  orientationStampExhausted = false,
  onOrientationContinueAnyway,
  orientationRejectLatch = false,
  orientationRejectChoice = null,
  orientationPausedAtSeconds = null,
  onOrientationRejectFixNow,
  onOrientationRejectFinishFirst,
  onOrientationPauseOffset,
}: LobbyStepProps) {
  const { t } = useTranslation();
  const idSubmitted = runState.idVerification?.status === 'submitted';
  const idRejected = runState.idVerification?.status === 'rejected';
  const showReviewNote = idSubmitted;
  const confirmedNoFilm = loungeOrientationResolved && orientationVideoUrl === null;
  const showPlayer = typeof orientationVideoUrl === 'string' && orientationVideoUrl.length > 0;
  const showLoading = !loungeOrientationResolved && !loungeFetchFailed && !confirmedNoFilm;
  const showLoadFailed = loungeFetchFailed;

  const showRejectOverlay = Boolean(
    orientationRejectLatch && idRejected && orientationRejectChoice === null,
  );
  const finishFirstChosen = orientationRejectChoice === 'finishFirst';
  const showRetakeReminder = Boolean(orientationRejectLatch && idRejected && finishFirstChosen);
  const skipLabelKey = finishFirstChosen
    ? 'oralExam.lobby.orientation.skipAndRetake'
    : 'oralExam.lobby.waiting.orientationSkip';

  const stampInProgress = isCompletingOrientation || orientationStampRetrying;
  const skipDisabled = stampInProgress || orientationStampExhausted;
  const showSaveRetry = stampInProgress && !orientationStampExhausted;

  const handleSkipOrContinue = () => {
    onOrientationComplete?.();
  };

  return (
    <VStack
      space="lg"
      p="$4"
      alignItems="center"
      flex={1}
      testID="oral-exam-lobby-orientation-step"
    >
      <VStack space="sm" alignItems="center" maxWidth={480}>
        <Text size="xl" fontWeight="semibold" color="$textHeading" textAlign="center">
          {t('oralExam.lobby.orientation.title')}
        </Text>
        <Text size="md" color="$textBody" textAlign="center">
          {t('oralExam.lobby.orientation.body')}
        </Text>
        {showReviewNote ? (
          <Text size="sm" color="$textSubtle" textAlign="center">
            {t('oralExam.lobby.orientation.reviewNote')}
          </Text>
        ) : null}
      </VStack>

      {showLoading ? (
        <VStack space="md" alignItems="center" width="100%" maxWidth={560} accessibilityLiveRegion="polite">
          <Spinner size="large" />
          <Text size="md" color="$textSubtle" textAlign="center">
            {t('oralExam.lobby.orientation.loading')}
          </Text>
          {onOrientationComplete ? (
            <Button
              variant="outline"
              size="sm"
              alignSelf="flex-end"
              onPress={handleSkipOrContinue}
              isDisabled={skipDisabled}
              testID="oral-exam-orientation-skip"
            >
              <ButtonText>{t(skipLabelKey)}</ButtonText>
            </Button>
          ) : null}
        </VStack>
      ) : null}

      {showLoadFailed ? (
        <VStack
          space="md"
          alignItems="center"
          width="100%"
          maxWidth={560}
          accessibilityLiveRegion="polite"
          testID="oral-exam-orientation-load-failed"
        >
          <Icon as={AlertTriangle} size="lg" color="$statusWarning" />
          <Text size="md" fontWeight="semibold" color="$textHeading" textAlign="center">
            {t('oralExam.lobby.orientation.loadFailed')}
          </Text>
          <Text size="sm" color="$textSubtle" textAlign="center">
            {t('oralExam.lobby.orientation.loadFailedBody')}
          </Text>
          {onOrientationComplete ? (
            <Button
              size="sm"
              action="primary"
              onPress={handleSkipOrContinue}
              isDisabled={stampInProgress}
              testID="oral-exam-orientation-continue"
            >
              <ButtonText>{t('oralExam.lobby.orientation.continue')}</ButtonText>
            </Button>
          ) : null}
        </VStack>
      ) : null}

      {showRetakeReminder ? (
        <Box
          width="100%"
          maxWidth={560}
          bg="$statusWarningSubtle"
          borderLeftWidth={4}
          borderLeftColor="$statusWarning"
          p="$4"
          testID="oral-exam-orientation-retake-reminder"
        >
          <Text size="sm" color="$textOnWarningSubtle">
            {t('oralExam.lobby.orientation.reject.reminder')}
          </Text>
        </Box>
      ) : null}

      {showPlayer && onOrientationComplete ? (
        <Box position="relative" width="100%" maxWidth={560} alignItems="center">
          <LobbyOrientationPlayer
            videoUrl={orientationVideoUrl}
            onComplete={onOrientationComplete}
            isCompleting={isCompletingOrientation}
            skipDisabled={skipDisabled}
            paused={showRejectOverlay}
            startPositionSeconds={orientationPausedAtSeconds}
            onPauseOffset={onOrientationPauseOffset}
            hideSkip={showRejectOverlay}
            skipLabelKey={skipLabelKey}
          />
          {showRejectOverlay && onOrientationRejectFixNow && onOrientationRejectFinishFirst ? (
            <OrientationRejectOverlay
              runState={runState}
              onFixNow={onOrientationRejectFixNow}
              onFinishFirst={onOrientationRejectFinishFirst}
            />
          ) : null}
        </Box>
      ) : null}

      {showSaveRetry ? (
        <Text
          size="sm"
          color="$textSubtle"
          textAlign="center"
          testID="oral-exam-orientation-save-retry"
        >
          {t('oralExam.lobby.orientation.saveRetry')}
        </Text>
      ) : null}

      {orientationStampExhausted && onOrientationContinueAnyway ? (
        <Button
          size="sm"
          action="primary"
          onPress={onOrientationContinueAnyway}
          testID="oral-exam-orientation-continue-anyway"
        >
          <ButtonText>{t('oralExam.lobby.orientation.continueAnyway')}</ButtonText>
        </Button>
      ) : null}
    </VStack>
  );
}
